import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, streams } from '@/mocks/mogu-data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'game');

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug),
    [activeSlug]
  );

  const filteredStreams = useMemo(
    () => streams.filter((s) => s.category === activeSlug),
    [activeSlug]
  );

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: currentCategory?.name || '分类' },
            ]}
          />

          <div className="mb-10">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              {currentCategory?.name || '分类'}
            </h1>
            <p className="mt-2 text-sm text-foreground-600">
              共{filteredStreams.length}个精彩直播内容
            </p>
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveSlug(cat.slug)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 pb-16">
            {filteredStreams.map((stream) => (
              <div
                key={stream.id}
                className="group rounded-xl overflow-hidden bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer"
              >
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={stream.thumbnail}
                    alt={stream.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                    loading="lazy"
                  />
                  <div className="absolute top-3 left-3">
                    {stream.isLive ? (
                      <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-semibold">
                        <span className="w-1.5 h-1.5 rounded-full bg-background-50 animate-pulse"></span>
                        直播中
                      </span>
                    ) : (
                      <span className="px-2.5 py-1 rounded-full bg-background-800/60 text-background-50 text-xs">
                        回放
                      </span>
                    )}
                  </div>
                  <div className="absolute bottom-3 right-3">
                    <span className="px-2 py-0.5 rounded-full bg-black/60 text-background-50 text-xs">
                      <i className="ri-eye-line mr-1"></i>
                      {stream.viewers > 10000
                        ? `${(stream.viewers / 10000).toFixed(1)}万`
                        : stream.viewers}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 leading-snug">
                    {stream.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-3">
                    <img
                      src={stream.anchorAvatar}
                      alt={stream.anchor}
                      className="w-6 h-6 rounded-full object-cover"
                      loading="lazy"
                    />
                    <span className="text-xs text-foreground-600">{stream.anchor}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    {stream.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 rounded-md bg-background-200 text-foreground-600 text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredStreams.length === 0 && (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-5xl text-foreground-300"></i>
              <p className="mt-4 text-foreground-500">该分类暂无直播内容</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}